import React from 'react';
import { Box, Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { 
  ViewColumn as ViewColumnIcon, 
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useSidebar } from './Layout';

const TableToolbar = ({ title, showFullTableButton = true, showToggleSidebarButton = true }) => {
  const { sidebarCollapsed, fullTableMode, toggleSidebar, showFullTable, hideFullTable } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: 'space-between', 
      alignItems: { xs: 'flex-start', sm: 'center' },
      gap: { xs: 2, sm: 0 },
      marginBottom: { xs: 0, sm: 3 },
      padding: { xs: 2, sm: 3 },
      backgroundColor: 'white',
      borderRadius: { xs: 0, sm: 3 },
      boxShadow: { xs: 'none', sm: '0 4px 16px rgba(0,0,0,0.1)' },
      border: { xs: 'none', sm: '1px solid #e0e0e0' },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: { xs: 'none', sm: '0 8px 32px rgba(0,0,0,0.15)' },
        transform: { xs: 'none', sm: 'translateY(-2px)' }
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, paddingLeft: 0 }}>
        {title && (
          <h2 style={{ 
            margin: 0, 
            fontSize: 'clamp(1.25rem, 4vw, 2rem)',
            fontWeight: '700',
            color: '#4e0159',
            letterSpacing: '-0.5px'
          }}>
            {title}
          </h2>
        )}
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 1 },
        width: { xs: '100%', sm: 'auto' },
        paddingRight: { xs: 0, sm: 2 }
      }}>
        {showToggleSidebarButton && !isMobile && (
          <Tooltip title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}>
            <Button
              variant="outlined"
              startIcon={<MenuIcon />}
              onClick={toggleSidebar}
              size="medium"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                border: '2px solid #1976d2',
                color: '#1976d2',
                '&:hover': {
                  background: '#1976d2',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)'
                }
              }}
            >
              {sidebarCollapsed ? 'Show Sidebar' : 'Hide Sidebar'}
            </Button>
          </Tooltip>
        )}
        
        {showFullTableButton && (
          <Tooltip title={fullTableMode ? "Exit Full Table View" : "View All Columns"}>
            <Button
              variant="contained"
              startIcon={fullTableMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
              onClick={fullTableMode ? hideFullTable : showFullTable}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: { xs: 0.75, sm: 1 },
                background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0, #1976d2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 32px rgba(25, 118, 210, 0.4)'
                }
              }}
            >
              {fullTableMode ? 'Exit Full View' : 'View All Columns'}
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default TableToolbar;
